import { useState, useEffect } from 'react';
import { connect, RootStateOrAny } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { useHistory } from 'react-router';
import moment from "moment";

import { Button, Typography, Divider, Table, Image, Skeleton, Modal } from "antd";
import { PrinterOutlined } from '@ant-design/icons';

import notifier from 'utils/notifier';
import { formatPhoneNumber, toLetters, getDisplayCompanyId } from 'utils/helpers';
import { ErrorResponse } from 'utils/types';

import { get as getPortalClaim, getUsingEdifecs as getPortalClaimUsingEdifecs } from 'store/claim/portal/actions';
import { getAttachments } from 'store/claim/member/actions';

import astranaLogoPlum  from 'assets/images/Astrana_Logo_Plum.svg';
import "./index.scss";


const { Title } = Typography;

interface ClaimDetailProps {
  claimNo: string,
  setClaimNo(claimNo: string): void
  status?: string,
  companyId: string,

  getPortalClaim(claimNo: string, companyId: string): any,
  getPortalClaimUsingEdifecs(claimNo: string, companyId: string): any,
  isFetchingPortalClaim: boolean,
  getAttachments(claimNo: string, companyId: string): any,
  isFetchingAttachments: boolean,
  setPage(page: string): any,
}

const ClaimDetail = (props: ClaimDetailProps) => {
  const history = useHistory();
  const [portalClaimMaster, setPortalClaimMaster] = useState<any>({}); // any type is fine here, portalClaimMaster object is massive.
  const [portalClaimDiags, setPortalClaimDiags] = useState([]);
  const [portalClaimDetails, setPortalClaimDetails] = useState([]);
  const [attachmentList, setAttachmentList] = useState<Array<string>>([]);


  useEffect(() => {
    getPortalClaimData();
    getClaimAttachments();

    window.addEventListener("popstate", backEvent);
    return () => {
      window.removeEventListener("popstate", backEvent);
    }
  }, []);

  const backEvent = (e: any) => {
    if (e.state === null) {
      e.preventDefault();
      return false;
    }

    history.push('/claims/submitted-claims');
    return props.setPage('start');
  }

  const getPortalClaimData = () => {
    return props.getPortalClaimUsingEdifecs(props.claimNo, props.companyId)
      .then((response: any) => {
        // any type is fine here
        const status = response?.portalClaimMaster?.RmiStatus;
        const pipeIndex = status?.indexOf("|");
        if (pipeIndex && pipeIndex > 0) {
          response.portalClaimMaster.RmiEdifecsError = status?.substring(pipeIndex + 1, status?.length);
          response.portalClaimMaster.RmiStatus = status?.substring(0, pipeIndex);
        } else {
          response.portalClaimMaster.RmiEdifecsError = "";
        }
        setPortalClaimMaster(response.portalClaimMaster);
        setPortalClaimDiags(response.portalClaimDiags);
        setPortalClaimDetails(response.portalClaimDetails);
        localStorage.setItem("print_ipa", props.companyId);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getClaimAttachments = () => {
    return props.getAttachments(props.claimNo, props.companyId)
      .then((response: Array<string>) => {
        setAttachmentList(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const onPrint = () => {
    window.print();
  };

  const showClaimErrorDescription = () => {
    return Modal.info(
      {
        title: "Claim Error",
        content: portalClaimMaster.RmiEdifecsError,
        type: "info",
        maskClosable: false,
      });
  };

  const diagColumns = [
    {
      title: 'DIAG REF',
      key: 'RmiIdx',
      dataIndex: 'RmiIdx',
      render: (text: string) => toLetters(parseInt(text))
    },
    {
      title: 'DIAGNOSIS CODE',
      key: 'RmiDiagCode',
      dataIndex: 'RmiDiagCode',
    },
    {
      title: 'DESCRIPTION',
      key: 'RmiDiagDesc',
      dataIndex: 'RmiDiagDesc',
    },
  ];

  const procColumns = [
    {
      title: 'SUPPL. INFO',
      key: 'RmiDetailNote',
      dataIndex: 'RmiDetailNote',
    },
    {
      title: <>FROM<br />TO</>,
      key: 'RmiDosFrom',
      dataIndex: 'RmiDosFrom',
      render: (text: string, record: any) => {
        if (record.key === 'summary') return;
        return (
            <>
            {moment(text).format('MM/DD/YYYY')}<br />{moment(record.RmiDosTo).format('MM/DD/YYYY')}<br/><span>{record.RmiTotalTime ? `${record.RmiTotalTime} minutes` : ''}</span>
            </>
          );
        }
    },
    {
      title: 'POS',
      key: 'RmiPosCode',
      dataIndex: 'RmiPosCode',
    },
    {
      title: 'EMG',
      key: 'RmiEmg',
      dataIndex: 'RmiEmg',
    },
    {
      title: 'CPT',
      key: 'RmiCptCode',
      dataIndex: 'RmiCptCode',
    },
    {
      title: 'MODIFIERS',
      key: 'RmiCptModifier1',
      dataIndex: 'RmiCptModifier1',
      render: (text: string, record: any) => <>{text} {record.RmiCptModifier2} {record.RmiCptModifier3} {record.RmiCptModifier4}</>
    },
    {
      title: 'DIAG REF',
      key: 'RmiDiagPtr',
      dataIndex: 'RmiDiagPtr',
      render: (text: string, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Patient Account
              <br />
              {portalClaimMaster.RmiPatientAcctNo ? portalClaimMaster.RmiPatientAcctNo : <>&nbsp;</>}
            </div>
          );
        }
        return (
          <>
            {text ? toLetters(parseInt(text)) : null}&nbsp;
            {record.RmiDiagPtr2 ? toLetters(parseInt(record.RmiDiagPtr2)) : null}&nbsp;
            {record.RmiDiagPtr3 ? toLetters(parseInt(record.RmiDiagPtr3)) : null}&nbsp;
            {record.RmiDiagPtr4 ? toLetters(parseInt(record.RmiDiagPtr4)) : null}
          </>
        )
      }
    },
    {
      title: 'CHARGES',
      key: 'RmiCharges',
      dataIndex: 'RmiCharges',
      render: (value: number, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Total Charge
              <br />
              {`$${parseFloat(portalClaimMaster?.RmiTotalCharge).toFixed(2)}`}
            </div>
          )
        } else return `$${value.toFixed(2)}`;
      }
    },
    {
      title: 'DAYS OR UNITS',
      key: 'RmiDays',
      dataIndex: 'RmiDays',
      render: (value: number, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Amount Paid
              <br />
              {`$${parseFloat(portalClaimMaster?.RmiAmountPaid).toFixed(2)}`}
            </div>
          )
        } else return value;
      }
    },
    {
      title: 'PROVIDER NPI',
      key: 'RmiRendProviderNpi',
      dataIndex: 'RmiRendProviderNpi',
      render: (value: string, record: any) => {
        if (record.key === 'summary') {
          return (
            <div style={{ fontWeight: 'bolder' }}>
              Balance
              <br />
              {`$${parseFloat(portalClaimMaster?.RmiBalanceDue).toFixed(2)}`}
            </div>
          )
        } else return value;
      }
    },
  ];

  const summary = {
    key: 'summary',
    RmiDosFrom: 'summary',
    RmiDiagPtr: 'summary',
    RmiCharges: 'summary',
    RmiDays: 'summary',
    RmiRendProviderNpi: 'summary',
  };

  return (
    <>
    { props.isFetchingPortalClaim || props.isFetchingAttachments ? (
      <>
       <Skeleton
         active
         paragraph={{ rows: 4 }}
       />
       <br />
       <Skeleton
         active
         avatar
         paragraph={{ rows: 8 }}
       />
       <br />
       <Skeleton
         active
         paragraph={{ rows: 4 }}
       />
      </>
    ) : (
    <div className="row flex-lg-wrap justify-content-center mb-3 ml-0 mt-3 print-submitted-claims-info">

      <div className="only-print">
        <div className="d-flex align-items-center" style={{ width: '50%', float: 'left' }}>
          <Image
            src={astranaLogoPlum}
            width={'150px'}
          />
        </div>
      </div>

      <div className="print-center col-print-12">
        <Title level={4}>Claim Detail</Title>
      </div>

      <div className="claim-detail row flex-lg-wrap justify-content-start">
      <div className="col-lg-18 col-md-18 mt-2" />

        <div className="col-lg-6 col-md-6 mt-2 row no-print">
          <div className="btn-wrapper col-lg">
            <Button type="primary" style={{ width: '100%' }} onClick={onPrint}><PrinterOutlined /> Print</Button>
          </div>
        </div>

        <div className="row flex-lg-wrap justify-content-center mb-3 ml-0 mt-3">
          <div className="col-lg-24 mt-2">
            <Title level={5}>
              Claim Reference Number <span style={{ color: 'green' }}>{getDisplayCompanyId(portalClaimMaster.RmiCompanyId)} - {portalClaimMaster.RmiClaimReferenceNo}</span>
            </Title>
          </div>

          <div className="col-lg-12 col-md-12 mt-2 mb-4 col-print-6">
            <Title level={5}>Patient's Information</Title>
            <Divider />
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr key="claimNo">
                  <th>Claim Ref No:</th>
                  <td>{getDisplayCompanyId(portalClaimMaster.RmiCompanyId)} - {portalClaimMaster.RmiClaimReferenceNo}</td>
                </tr>
                <tr key="name">
                  <th>Name:</th>
                  <td>{portalClaimMaster.RmiPatientName}</td>
                </tr>
                <tr key="address">
                  <th>Address:</th>
                  <td>{portalClaimMaster.RmiPatientAddress}</td>
                </tr>
                <tr key="cityStateZip">
                  <th>City/State/Zip:</th>
                  <td>
                    {portalClaimMaster.RmiPatientCity}, {portalClaimMaster.RmiPatientState} {portalClaimMaster.RmiPatientZip}
                  </td>
                </tr>
                <tr key="phone">
                  <th>Phone:</th>
                  <td>{formatPhoneNumber(portalClaimMaster.RmiPatientPhone)}</td>
                </tr>
                <tr key="birthDate">
                  <th>Birth Date:</th>
                  <td>{portalClaimMaster.RmiPatientBirthDate ? moment(portalClaimMaster.RmiPatientBirthDate).format('MM/DD/YYYY') : null}</td>
                </tr>

                <tr key="sex">
                  <th>Sex:</th>
                  <td>{portalClaimMaster.RmiInsuredSex}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12 mt-2 col-print-6">
            <Title level={5}>Patient's Condition Related To</Title>
            <Divider />
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr key="employment">
                  <th>Employment:</th>
                  <td>{portalClaimMaster.RmiEmploymentRelated}</td>
                </tr>
                <tr key="unableToWork">
                  <th>Unable to work in current occupation:</th>
                  <td>
                    {portalClaimMaster.RmiDateNoWorkFrom ? 'From: ' + moment(portalClaimMaster.RmiDateNoWorkFrom).format('MM/DD/YYYY') : null}
                    &nbsp;&nbsp;{portalClaimMaster.RmiDateNoWorkTo ? ` To: ${moment(portalClaimMaster.RmiDateNoWorkTo).format('MM/DD/YYYY')}` : null}
                  </td>
                </tr>
                <tr key="auto">
                  <th>Auto Accident:</th>
                  <td>{portalClaimMaster.RmiAutoRelated}</td>
                </tr>
                <tr key="autoPlace">
                  <th>Place (State):</th>
                  <td>
                    {portalClaimMaster.RmiAutoAccidentState}
                  </td>
                </tr>
                <tr key="phone">
                  <th>Other Accident:</th>
                  <td>{portalClaimMaster.RmiOtherRelated}</td>
                </tr>
                {portalClaimMaster.RmiEdifecsError !== "" ?
                <>
                  <tr key="rmiStatus">
                    <th>Status:</th>
                    <td><a onClick={()=> showClaimErrorDescription()}>{portalClaimMaster.RmiStatus }</a></td>
                  </tr>
                </>
                :
                portalClaimMaster.RmiStatus &&
                <>
                  <tr key="rmiStatus">
                    <th>Status:</th>
                    <td>{portalClaimMaster.RmiStatus }</td>
                  </tr>
                </>
                }
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12 mb-2 col-print-6">
            <Title level={5}>Insured's Information</Title>
            <Divider />
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr key="1">
                  <th>ID Number:</th>
                  <td>{portalClaimMaster.RmiInsuredId}</td>
                </tr>
                <tr key="2">
                  <th>Name:</th>
                  <td>{portalClaimMaster.RmiInsuredName}</td>
                </tr>
                <tr key="3">
                  <th>Address:</th>
                  <td>{portalClaimMaster.RmiInsuredAddress}</td>
                </tr>
                <tr key="4">
                  <th>City/State/Zip:</th>
                  <td>
                    {portalClaimMaster.RmiInsuredCity}, {portalClaimMaster.RmiInsuredState} {portalClaimMaster.RmiInsuredZip}
                  </td>
                </tr>
                <tr key="5">
                  <th>Phone:</th>
                  <td>{formatPhoneNumber(portalClaimMaster.RmiInsuredPhone)}</td>
                </tr>
                <tr key="6">
                  <th>Birth Date:</th>
                  <td>{portalClaimMaster.RmiInsuredDob ? moment(portalClaimMaster.RmiInsuredDob).format('MM/DD/YYYY') : null}</td>
                </tr>

                <tr key="7">
                  <th>Patient Relationship:</th>
                  <td>{portalClaimMaster.RmiPatRel}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12 mb-2 col-print-6">
            <Title level={5}>Other Insurance</Title>
            <Divider />
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr key="claimNo">
                  <th>Other Insured's Name:</th>
                  <td>{portalClaimMaster.RmiOtherInsuredName}</td>
                </tr>
                <tr key="name">
                  <th>Other Insured's Policy or Group No.:</th>
                  <td>{portalClaimMaster.RmiOtherInsuredPolicy}</td>
                </tr>
                <tr key="address">
                  <th>Insurance Plan or Program Name:</th>
                  <td>{portalClaimMaster.RmiInsuredOtherPlan}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <Divider />

          <div className="col-lg-24 col-md-24">
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <th>Date of Current Illness, Injury, <br />or Pregnancy (LMP)</th>
                <th>Referring Provider</th>
                <th>Hospitalization From</th>
                <th>Resubmission Code</th>
                <th>Outside Lab?</th>
              </thead>
              <tbody>
                <tr key="1">
                  <td>{portalClaimMaster.RmiDateOfCurrent ? moment(portalClaimMaster.RmiDateOfCurrent).format('MM/DD/YYYY') : null}</td>
                  <td>{portalClaimMaster.RmiReferringProvider}</td>
                  <td>{portalClaimMaster.RmiHospitalizedFrom ? moment(portalClaimMaster.RmiHospitalizedFrom).format('MM/DD/YYYY') : null}</td>
                  <td>{portalClaimMaster.RmiMedicaidResubCode}</td>
                  <td>{portalClaimMaster.RmiOutsideLab}</td>
                </tr>
              </tbody>
            </table>

            <br />

            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <thead>
                <th>Other Date</th>
                <th>NPI</th>
                <th>Hospitalization To</th>
                <th>Original Ref No</th>
                <th>Prior Auth No</th>
              </thead>
              <tbody>
                <tr key="1">
                  <td>{portalClaimMaster.RmiDateOfSimilar ? moment(portalClaimMaster.RmiDateOfSimilar).format('MM/DD/YYYY') : null}</td>
                  <td>{portalClaimMaster.RmiReferringProviderNpi}</td>
                  <td>{portalClaimMaster.RmiHospitalizedTo ? moment(portalClaimMaster.RmiHospitalizedTo).format('MM/DD/YYYY') : null}</td>
                  <td>{portalClaimMaster.RmiMedicaidOrigRefNo}</td>
                  <td>{portalClaimMaster.RmiPriorAuthNo}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-24 mb-1 mt-4">
            <Title level={5}>Notes</Title>
            {portalClaimMaster.RmiClaimNote && <>{portalClaimMaster.RmiClaimNote}</>}
            <Divider />
          </div>

          <div className="col-lg-24 mb-4 mt-4">
            <Title level={5}>Diagnosis Information</Title>
            <Table
              columns={diagColumns}
              dataSource={portalClaimDiags}
              pagination={false}
              rowKey={'RmiIdx'}
            />
          </div>

          <div className="col-lg-24 mb-4">
            <Title level={5}>Procedure Information</Title>
            {portalClaimDetails?.length !== 0 && (
              <Table
                columns={procColumns}
                dataSource={[...portalClaimDetails, summary]}
                pagination={false}
                rowKey={'RmiCptCode'}
              />
            )}
          </div>

          <div className="col-lg-24 mb-4">
            <Title level={5}>Attachments</Title>
            <ul>
              {attachmentList.map(attachment => <li>{attachment}</li>)}
            </ul>
          </div>

          <Divider />

          <div className="col-lg-12 col-md-12 mb-4 col-print-6">
            <Title level={5}>Billing Provider Information</Title>
            <Divider />
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr key="1">
                  <th>NPI:</th>
                  <td>{portalClaimMaster.RmiBillingProviderNpi}</td>
                </tr>
                <tr key="2">
                  <th>Tax Id:</th>
                  <td>{portalClaimMaster.RmiFedTaxId}</td>
                </tr>
                <tr key="3">
                  <th>Name:</th>
                  <td>{portalClaimMaster.RmiBillingProviderName}</td>
                </tr>
                <tr key="4">
                  <th>Address:</th>
                  <td>{portalClaimMaster.RmiBillingProviderAddress}</td>
                </tr>
                <tr key="5">
                  <th>City/State/Zip:</th>
                  <td>
                    {portalClaimMaster.RmiBillingProviderCity}, {portalClaimMaster.RmiBillingProviderState} {portalClaimMaster.RmiBillingProviderZip}
                  </td>
                </tr>
                <tr key="6">
                  <th>Phone:</th>
                  <td>{formatPhoneNumber(portalClaimMaster.RmiBillingProviderPhone)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="col-lg-12 col-md-12 mb-4 col-print-6">
            <Title level={5}>Facility Information</Title>
            <Divider />
            <table className="test"  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
              <tbody>
                <tr key="1">
                  <th>NPI:</th>
                  <td>{portalClaimMaster.RmiFacilityNpi}</td>
                </tr>
                <tr key="3">
                  <th>Name:</th>
                  <td>{portalClaimMaster.RmiFacilityName}</td>
                </tr>
                <tr key="4">
                  <th>Address:</th>
                  <td>{portalClaimMaster.RmiFacilityAddress}</td>
                </tr>
                <tr key="5">
                  <th>City/State/Zip:</th>
                  <td>
                    {portalClaimMaster.RmiFacilityCity}, {portalClaimMaster.RmiFacilityState} {portalClaimMaster.RmiFacilityZip}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    )}
    </>
  );
};


const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingPortalClaim: store.rootReducer.claimPortal.isFetching,
  isFetchingAttachments: store.rootReducer.claimMember.isFetchingAttachments,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
{
  getPortalClaim,
  getPortalClaimUsingEdifecs,
  getAttachments,
},
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(ClaimDetail);